import React from "react";
import { AppProvider } from "@context/AppContext";

import GTM from "@utils/GTM";
import { scrollToSection } from "@utils/helper";
import { normalizePathname } from "@utils/pathname-canonical";

import "./src/styles/global.css";
import "./src/styles/cookies.css";

/** Hard navigation: fallback if early <head> script did not run */
export const onClientEntry = () => {
  const { pathname, search, hash } = window.location;
  const normalized = normalizePathname(pathname);
  if (normalized !== pathname) {
    window.location.replace(normalized + search + hash);
  }
};

/** Client-side link click: runs before new route renders */
export const onPreRouteUpdate = ({ location }) => {
  if (!location) return;
  // Preserve in-page hash navigation timing; hard replace here is interrupted first anchor scroll.
  if (location.hash) return;
  const normalized = normalizePathname(location.pathname);
  if (normalized !== location.pathname) {
    window.location.replace(
      normalized + location.search + location.hash
    );
  }
};

/** Gatsby’s default hash scroll often runs before layout is ready; PageNav clicks feel “fixed” because they scroll later. */
export const shouldUpdateScroll = ({ routerProps }) => {
  const hash = routerProps?.location?.hash;
  return !hash;
};

const getScrollOffsetError = (id) => {
  const target = document.getElementById(id);
  if (!target) return 0;

  const cs = window.getComputedStyle(target);
  const marginTop = parseFloat(cs.scrollMarginTop) || 0;
  const rect = target.getBoundingClientRect();
  const expectedY = Math.max(0, rect.top + window.scrollY - marginTop);
  return Math.abs(window.scrollY - expectedY);
};

const scrollToHashWhenReady = (hash, { resetScroll = false } = {}) => {
  if (!hash) return;

  const id = decodeURIComponent(hash.replace(/^#/, ""));
  const selector = `#${id}`;
  const maxAttempts = 30; // 30 * 100ms ~= 3s
  let attempts = 0;

  // Reset scroll top for cross-page hash navigation.
  if (resetScroll && typeof window !== "undefined") {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  const tryScroll = () => {
    const target = document.getElementById(id);
    if (target) {
      scrollToSection(selector);
      // Correct once only when final anchor offset is visibly off.
      window.addEventListener(
        "smooth-scroll-complete",
        () => {
          if (getScrollOffsetError(id) > 8) {
            scrollToSection(selector);
          }
        },
        { once: true },
      );
      return;
    }

    attempts += 1;
    if (attempts < maxAttempts) {
      setTimeout(tryScroll, 100);
    }
  };

  requestAnimationFrame(() => {
    requestAnimationFrame(tryScroll);
  });
};

export const onInitialClientRender = () => {
  scrollToHashWhenReady(window.location.hash);
};

let lastPathname = typeof window !== "undefined" ? window.location.pathname : null;

export const onRouteUpdate = ({ location }) => {
  GTM.reset();
  const newPathname = location?.pathname ?? null;
  const isCrossPageNav = lastPathname !== null && newPathname !== lastPathname;
  lastPathname = newPathname;
  scrollToHashWhenReady(location?.hash, { resetScroll: isCrossPageNav });
};

export const wrapRootElement = ({ element }) => (
  <AppProvider>{element}</AppProvider>
);
